import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Grid,
  Typography,
  Divider,
  Alert,
  InputAdornment,
  Card,
  CardContent,
} from '@mui/material';
import TaxService from '../../services/TaxService';

function TaxConfigurationForm({ configuration, onSuccess, onCancel }) {
  const [formData, setFormData] = useState({
    taxName: '',
    taxRate: '',
    taxType: 'Percentage',
    fixedAmount: '',
    isDefault: false,
    isActive: true,
    description: '',
    applyToConsultation: true,
    applyToProcedure: true,
    applyToMedication: true,
    applyToService: true,
    minimumAmount: '',
    maximumAmount: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    if (configuration) {
      setFormData({
        taxName: configuration.taxName || '',
        taxRate: configuration.taxRate || '',
        taxType: configuration.taxType || 'Percentage',
        fixedAmount: configuration.fixedAmount || '',
        isDefault: configuration.isDefault || false,
        isActive: configuration.isActive !== undefined ? configuration.isActive : true,
        description: configuration.description || '',
        applyToConsultation: configuration.applyToConsultation !== undefined ? configuration.applyToConsultation : true,
        applyToProcedure: configuration.applyToProcedure !== undefined ? configuration.applyToProcedure : true,
        applyToMedication: configuration.applyToMedication !== undefined ? configuration.applyToMedication : true,
        applyToService: configuration.applyToService !== undefined ? configuration.applyToService : true,
        minimumAmount: configuration.minimumAmount || '',
        maximumAmount: configuration.maximumAmount || '',
      });
    }
  }, [configuration]);

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.taxName.trim()) {
      errors.taxName = 'Tax name is required';
    }

    if (formData.taxType === 'Percentage') {
      if (!formData.taxRate || formData.taxRate <= 0) {
        errors.taxRate = 'Tax rate must be greater than 0';
      } else if (formData.taxRate > 100) {
        errors.taxRate = 'Tax rate cannot exceed 100%';
      }
    } else if (formData.taxType === 'Fixed') {
      if (!formData.fixedAmount || formData.fixedAmount <= 0) {
        errors.fixedAmount = 'Fixed amount must be greater than 0';
      }
    }

    if (formData.minimumAmount && formData.maximumAmount) {
      if (parseFloat(formData.minimumAmount) >= parseFloat(formData.maximumAmount)) {
        errors.maximumAmount = 'Maximum amount must be greater than minimum amount';
      }
    }

    if (formData.minimumAmount && parseFloat(formData.minimumAmount) < 0) {
      errors.minimumAmount = 'Minimum amount cannot be negative';
    }

    if (formData.maximumAmount && parseFloat(formData.maximumAmount) < 0) {
      errors.maximumAmount = 'Maximum amount cannot be negative';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const payload = {
        ...formData,
        taxRate: parseFloat(formData.taxRate) || 0,
        fixedAmount: formData.fixedAmount ? parseFloat(formData.fixedAmount) : null,
        minimumAmount: formData.minimumAmount ? parseFloat(formData.minimumAmount) : null,
        maximumAmount: formData.maximumAmount ? parseFloat(formData.maximumAmount) : null,
      };

      if (configuration) {
        payload.taxConfigId = configuration.taxConfigId;
        await TaxService.updateTaxConfiguration(configuration.taxConfigId, payload);
        onSuccess('Tax configuration updated successfully');
      } else {
        await TaxService.createTaxConfiguration(payload);
        onSuccess('Tax configuration created successfully');
      }
    } catch (err) {
      setError('Failed to save tax configuration: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Basic Information */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Basic Information
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Tax Name"
            value={formData.taxName}
            onChange={handleChange('taxName')}
            error={!!validationErrors.taxName}
            helperText={validationErrors.taxName}
            required
            placeholder="e.g., GST, VAT, Sales Tax"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Tax Type</InputLabel>
            <Select
              value={formData.taxType}
              onChange={handleChange('taxType')}
              label="Tax Type"
            >
              <MenuItem value="Percentage">Percentage</MenuItem>
              <MenuItem value="Fixed">Fixed Amount</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {formData.taxType === 'Percentage' ? (
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Tax Rate"
              type="number"
              value={formData.taxRate}
              onChange={handleChange('taxRate')}
              error={!!validationErrors.taxRate}
              helperText={validationErrors.taxRate}
              required
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              inputProps={{ min: 0, max: 100, step: 0.01 }}
            />
          </Grid>
        ) : (
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Fixed Amount"
              type="number"
              value={formData.fixedAmount}
              onChange={handleChange('fixedAmount')}
              error={!!validationErrors.fixedAmount}
              helperText={validationErrors.fixedAmount}
              required
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              inputProps={{ min: 0, step: 0.01 }}
            />
          </Grid>
        )}

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Description"
            value={formData.description}
            onChange={handleChange('description')}
            multiline
            rows={2}
            placeholder="Optional description for this tax configuration"
          />
        </Grid>

        {/* Status Settings */}
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" gutterBottom>
            Status Settings
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive}
                onChange={handleChange('isActive')}
              />
            }
            label="Active"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.isDefault}
                onChange={handleChange('isDefault')}
              />
            }
            label="Set as Default Tax"
          />
        </Grid>

        {/* Applicability Settings */}
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" gutterBottom>
            Apply To Services
          </Typography>
        </Grid>

        <Grid item xs={12} md={3}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.applyToConsultation}
                onChange={handleChange('applyToConsultation')}
              />
            }
            label="Consultation"
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.applyToProcedure}
                onChange={handleChange('applyToProcedure')}
              />
            }
            label="Procedure"
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.applyToMedication}
                onChange={handleChange('applyToMedication')}
              />
            }
            label="Medication"
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.applyToService}
                onChange={handleChange('applyToService')}
              />
            }
            label="Service"
          />
        </Grid>

        {/* Threshold Settings */}
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" gutterBottom>
            Amount Thresholds (Optional)
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Minimum Amount"
            type="number"
            value={formData.minimumAmount}
            onChange={handleChange('minimumAmount')}
            error={!!validationErrors.minimumAmount}
            helperText={validationErrors.minimumAmount || 'Tax applies only if invoice amount is above this value'}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            inputProps={{ min: 0, step: 0.01 }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Maximum Amount"
            type="number"
            value={formData.maximumAmount}
            onChange={handleChange('maximumAmount')}
            error={!!validationErrors.maximumAmount}
            helperText={validationErrors.maximumAmount || 'Tax applies only if invoice amount is below this value'}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            inputProps={{ min: 0, step: 0.01 }}
          />
        </Grid>

        {/* Action Buttons */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
            <Button
              variant="outlined"
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
            >
              {loading ? 'Saving...' : (configuration ? 'Update' : 'Create')}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

export default TaxConfigurationForm;